import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EVENT_TYPES } from "@/lib/constants";
import { BRAND } from "@/lib/constants";
import { Send, Loader2, CheckCircle2, MessageCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ScheduleCall() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    event_type: "",
    preferred_date: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [lastLead, setLastLead] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.event_type) {
      toast.error("Please fill name, phone, email and event type.");
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/consultations`, form);
      setLastLead({ ...form });
      setDone(true);
      toast.success("Thank you — we'll reach out within 24 hours.");
      // Ads conversion tracking (no-op if trackers absent)
      if (typeof window !== "undefined") {
        if (typeof window.fbq === "function") {
          window.fbq("track", "Lead", {
            content_name: form.event_type,
            content_category: "Consultation",
          });
        }
        if (typeof window.gtag === "function") {
          window.gtag("event", "generate_lead", {
            event_category: "Consultation",
            event_label: form.event_type,
          });
        }
      }
      setForm({
        name: "",
        phone: "",
        email: "",
        event_type: "",
        preferred_date: "",
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please WhatsApp us directly.");
    } finally {
      setLoading(false);
    }
  };

  // Build a pre-filled WhatsApp "click to chat" link with all lead details
  const waLink = () => {
    if (!lastLead) return "#";
    const lines = [
      `Namaste ${BRAND.name} team,`,
      ``,
      `I just submitted an enquiry —`,
      `• *Name:* ${lastLead.name}`,
      `• *Event:* ${lastLead.event_type}`,
      `• *Phone:* ${lastLead.phone}`,
      `• *Email:* ${lastLead.email}`,
    ];
    if (lastLead.preferred_date) lines.push(`• *Date:* ${lastLead.preferred_date}`);
    if (lastLead.message) lines.push(`• *Notes:* ${lastLead.message}`);
    lines.push(``, `Looking forward to hearing from you.`);
    return `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(lines.join("\n"))}`;
  };

  return (
    <section
      id="schedule"
      data-testid="schedule-section"
      className="relative py-24 md:py-36 border-t border-white/5 overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, #1a1414 0%, #1e1a16 100%)",
      }}
    >
      <div
        aria-hidden="true"
        className="absolute top-20 right-0 w-[500px] h-[500px] pointer-events-none rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(212,175,55,0.14) 0%, rgba(0,0,0,0) 70%)",
          filter: "blur(30px)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute bottom-0 left-10 w-[400px] h-[400px] pointer-events-none rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(232,165,180,0.14) 0%, rgba(0,0,0,0) 70%)",
          filter: "blur(30px)",
        }}
      />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-[var(--iw-gold)]" />
              <span className="overline text-[var(--iw-gold)]">
                Request a Quote
              </span>
            </div>
            <h2
              className="font-serif-display text-[var(--iw-ivory)] text-5xl md:text-6xl leading-[0.95] tracking-tight"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              Let's design
              <br />
              <span className="italic gold-rose-gradient">
                your invite
              </span>
              .
            </h2>
            <p className="text-[var(--iw-muted)] text-base leading-relaxed mt-8 max-w-md">
              Tell us a bit about your event. We'll reply on WhatsApp within a
              few hours with sample reels, pricing and next steps.
            </p>

            <div className="mt-12 space-y-6">
              {[
                { k: "01", v: "You reach us on WhatsApp — a real person replies, fast." },
                { k: "02", v: "Share photos, venue, function names. We share sample reels." },
                { k: "03", v: "First design preview in 24 hours — revisions included." },
              ].map((m) => (
                <div
                  key={m.k}
                  className="flex items-start gap-5 border-l border-[var(--iw-border-gold)] pl-5"
                >
                  <span
                    className="font-serif-display italic text-[var(--iw-gold)] text-xl"
                    style={{ fontFamily: "Cormorant Garamond, serif" }}
                  >
                    {m.k}
                  </span>
                  <p className="text-[var(--iw-ivory-dim)] text-sm leading-relaxed">
                    {m.v}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Form */}
          <div className="lg:col-span-7">
            <motion.form
              onSubmit={submit}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="glass-card p-8 md:p-12 relative"
              data-testid="schedule-form"
            >
              {done && (
                <div className="absolute inset-0 z-10 grid place-items-center bg-black/80 backdrop-blur-sm p-8 text-center">
                  <div className="max-w-sm">
                    <CheckCircle2
                      className="text-[var(--iw-gold)] mx-auto mb-6"
                      size={56}
                      strokeWidth={1}
                    />
                    <h3
                      className="font-serif-display text-[var(--iw-ivory)] text-4xl leading-tight"
                      style={{ fontFamily: "Cormorant Garamond, serif" }}
                    >
                      With utmost <span className="italic text-[var(--iw-gold)]">gratitude</span>.
                    </h3>
                    <p className="text-[var(--iw-muted)] mt-4 leading-relaxed">
                      Your request has reached our atelier. Expect a call from
                      our founder within 24 hours.
                    </p>
                    <a
                      href={waLink()}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-gold mt-8 w-full justify-center"
                      data-testid="schedule-wa-send"
                    >
                      <MessageCircle size={14} />
                      Send details on WhatsApp
                    </a>
                    <button
                      onClick={() => setDone(false)}
                      className="btn-ghost-gold mt-3 w-full justify-center"
                      data-testid="schedule-reset"
                    >
                      Submit Another
                    </button>
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-5 md:gap-6">
                <Field label="Full Name" required>
                  <input
                    type="text"
                    className="input-luxe"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Your name"
                    data-testid="form-name"
                  />
                </Field>
                <Field label="Phone" required>
                  <input
                    type="tel"
                    className="input-luxe"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="+91"
                    data-testid="form-phone"
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    type="email"
                    className="input-luxe"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@example.com"
                    data-testid="form-email"
                  />
                </Field>
                <Field label="Event Type" required>
                  <Select
                    value={form.event_type}
                    onValueChange={(v) => update("event_type", v)}
                  >
                    <SelectTrigger
                      className="input-luxe h-[50px] rounded-none"
                      data-testid="form-event-type-trigger"
                    >
                      <SelectValue placeholder="Select an event" />
                    </SelectTrigger>
                    <SelectContent
                      className="border-white/15 rounded-none"
                      style={{
                        background: "#0a0a0a",
                        color: "var(--iw-ivory)",
                      }}
                    >
                      {EVENT_TYPES.map((t) => (
                        <SelectItem
                          key={t}
                          value={t}
                          className="font-sans-body"
                          data-testid={`form-event-type-${t}`}
                        >
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Preferred Date">
                  <input
                    type="date"
                    className="input-luxe"
                    value={form.preferred_date}
                    onChange={(e) => update("preferred_date", e.target.value)}
                    data-testid="form-date"
                  />
                </Field>
                <Field label="Budget / Notes" full>
                  <textarea
                    rows={4}
                    className="input-luxe resize-none"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="Tell us a little about your event, guests, or any references..."
                    data-testid="form-message"
                  />
                </Field>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-gold mt-8 w-full md:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="form-submit"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    Request a Quote
                    <Send size={14} />
                  </>
                )}
              </button>
              <p className="overline text-[var(--iw-faint)] mt-5">
                We reply within a few hours on WhatsApp · No spam, ever
              </p>
            </motion.form>
          </div>
        </div>
      </div>
    </section>
  );
}

const Field = ({ label, children, required, full }) => (
  <label
    className={`flex flex-col gap-2 ${full ? "md:col-span-2" : ""}`}
  >
    <span className="overline text-[var(--iw-faint)]">
      {label} {required && <span className="text-[var(--iw-gold)]">·</span>}
    </span>
    {children}
  </label>
);
